import moment from "moment";
import { useEffect, useState } from "react";
import { Typography, getAllDataCodename, useCommonComponentContext } from "@maysoft/common-component-react";

import Helpers from "../../commons/helpers";
import useStrings from "../../hooks/useStrings";
import TagService from "../../services/message/Tag.service";
import PostService from "../../services/message/Post.service";
import AssetService from "../../services/asset/Asset.service";
import SystemSettingService from "../../services/message/SystemSetting.service";

import { ICodename, IMultiLanguageContent } from "../../commons/interfaces";
import { IsTrue, PostIsHighlighted, PostType, PublishedNotification, PublishedStatus, TagType } from "../../constants/enums";


export interface IRequestGetPaged {
    postTag?: string;
    type?: PostType[];
    published?: boolean;
    isHighlighted?: PostIsHighlighted;
    pageSize: number;
    pageNumber: number;
    totalCount?: number;
    orderBy?: string;
}

export interface ISystemSettingValue {
    period: number;
    currency: string;
    tutorRegistrationMode: string;
    publishNotification: {
        course: PublishedNotification;
        post: PublishedNotification;
    };
}

export interface IPublishRequest {
    id: string;
    notification: PublishedNotification;
}

export interface IRecordPost {
    postTag?: string;
    postImages?: IPostVideo[];
    postVideos?: IPostVideo[];
    publishReady?: boolean;
    type?: number;
    tenantCode?: string;
    organizationId?: string;
    previewImage?: string;
    previewImageUrl?: string;
    title?: IMultiLanguageContent;
    content?: IMultiLanguageContent;
    shortDescription?: IMultiLanguageContent;
    metaHeader?: any;
    searchText?: string;
    publishedTime?: string;
    eventTime?: string;
    isHighlighted?: number;
    publishedStatus?: number;
    resourceCode?: string;
    notification?: number;
    channelId?: string;
    id?: string;
    status?: number;
    createTime?: string;
    createUser?: string;
    updateTime?: string;
    updateUser?: string;
}

export interface IPostVideo {
    fileId?: string;
    fileUrl?: string;
    sequence?: number;
    description?: IMultiLanguageContent;
}

export interface IDataDetail {
    id?: string;
    type?: number;
    postTag?: string;
    eventTime?: number;
    isHighlighted?: number;
    title?: IMultiLanguageContent;
    content?: IMultiLanguageContent;
    shortDescription?: IMultiLanguageContent;
    previewImage?: string;
    previewImageUrl?: string;
    postVideos?: IPostVideo[];
    postImages?: IPostVideo[];
    channelId?: string;
}

export type IDataError = { [key in keyof IDataDetail]?: string };

export interface IDetailRequest {
    id: string;
    published?: boolean;
}

export interface IDataCreateUpdate {
    tenantCode: string;
    id?: string;
    type?: number;
    previewImage?: number;
    previewImageUrl?: string;
    title?: IMultiLanguageContent;
    content?: IMultiLanguageContent;
    shortDescription?: IMultiLanguageContent;
    metaHeader?: string;
    postVideos?: IPostVideo[];
    publishedTime?: number;
    eventTime?: number;
    isHighlighted?: number;
    postTag?: {
        id?: number;
        name?: IMultiLanguageContent;
    };
    channelId?: number;
    resourceCode?: string;
}



const usePost = () => {
    const Strings = useStrings();

    const {
        language,
        tenantCode,
        serviceCode,
        listMultiLanguage,

        onError,
        onSuccess,
        onShowLoading,
        onHideLoading,
    } = useCommonComponentContext();

    const [publishRequestModel, setPublishRequestModel] = useState<IPublishRequest>();
    const [listPostTags, setListPostTags] = useState<ICodename[]>([]);
    const [listPostTypes, setListPostTypes] = useState<ICodename[]>([]);
    const listPostHighlighted: ICodename[] = [
        {
            code: PostIsHighlighted.None,
            name: Strings.Common.NO,
        },
        {
            code: PostIsHighlighted.Highlighted,
            name: Strings.Common.YES,
        },
    ];

    useEffect(() => {
        (async () => {
            const resultCode = await getAllDataCodename({
                group: ["postType"],
                serviceCode: serviceCode,
            });

            const postTypesTemp = (resultCode.get("postType") || []).map<ICodename>((type) => ({
                name: type.name,
                code: Number(type.code),
            }));

            setListPostTypes(postTypesTemp);

            const resultTag = await TagService.getAll({
                serviceCode: serviceCode,
                tenantCode: tenantCode,
                type: TagType.Post,
            });

            const postTagsTemp = resultTag.map<ICodename>((tag) => ({
                code: tag.id,
                name: tag.name?.value?.[language],
            }));

            setListPostTags(postTagsTemp);
        })();

        const loadSystemSettings = async () => {
            try {
                const result: any = await SystemSettingService.getBySettingCode("000002");
                const settingValue: ISystemSettingValue = JSON.parse(result.settingValue);

                const notification = settingValue?.publishNotification?.post;

                setPublishRequestModel((prev) => ({ ...prev, notification }));
            } catch (error) { }
        };
        loadSystemSettings();
    }, [language, tenantCode, serviceCode]);

    const getPublishedStatusValue = (props: { status: number; publishReady?: boolean }) => {
        let value: { label: string; color: string };

        switch (props.status) {
            case PublishedStatus.Unpublishing:
                value = {
                    label: Strings.POST.UNPUBLISHING,
                    color: "#a10202",
                };
                break;

            case PublishedStatus.Draft:
                value = {
                    label: Strings.POST.UNPUBLISHED,
                    color: "#FF0000",
                };
                if (props.publishReady === true) {
                    value.label = Strings.POST.PUBLISH_READY;
                }
                if (props.publishReady === false) {
                    value.label = Strings.POST.NOT_PUBLISH_READY;
                }
                break;

            case PublishedStatus.Publishing:
                value = {
                    label: Strings.POST.PUBLISHING,
                    color: "#d9d211",
                };
                break;

            case PublishedStatus.Published:
                value = {
                    label: Strings.POST.PUBLISHED,
                    color: "#27AE60",
                };
                break;

            default:
                value = {
                    label: Strings.POST.UNKNOWN,
                    color: "#000000",
                };
                break;
        }

        return (
            <Typography variant="caption" fontWeight="medium" sx={{ color: value.color }}>
                {value?.label || ""}
            </Typography>
        );
    };

    const getDetailPostById = async (postId: string, extraLang?: string) => {
        let newDetail: any = {
            id: undefined,
            type: undefined,
            postTag: undefined,
            previewImage: undefined,
            eventTime: moment().unix(),
            isHighlighted: IsTrue.False,
            title: { value: { [language]: "" } },
            content: { value: { [language]: "" } },
            shortDescription: { value: { [language]: "" } },
            postVideos: [],
        };

        try {
            if (!Helpers.isNullOrEmpty(postId)) {
                const result = await PostService.getDetail({ id: postId, published: false }, extraLang);

                let postVideos: IPostVideo[] = [];
                const fileIds = [...(result?.postVideos || [])]?.map((item: any) => item?.fileId) as string[];

                if (fileIds.length > 0) {
                    let urlMap: any = {};
                    const externalUrlIds = await AssetService.getExternalUrlIds(fileIds);

                    for (const item of externalUrlIds) {
                        urlMap[item.id] = item.url;
                    }

                    for (const item of [...(result?.postVideos || [])]) {
                        postVideos.push({
                            fileId: item.fileId,
                            sequence: item.sequence,
                            fileUrl: urlMap[item.fileId],
                        });
                    }
                }

                newDetail = {
                    id: result?.id,
                    type: result?.type,
                    title: result?.title,
                    content: result?.content,
                    postTag: result?.postTag,
                    previewImage: result?.previewImage,
                    eventTime: Number(result?.eventTime),
                    shortDescription: result?.shortDescription,
                    isHighlighted: Number(result?.isHighlighted),
                    postVideos: postVideos,
                };
            }
        } catch (error) {
            const err = Helpers.renderExceptionError(error);
            onError(err);
        }

        return newDetail;
    };

    const handlePublishPost = async (data: { postId: string; onCallBack?: () => void }) => {
        if (Helpers.isNullOrEmpty(data.postId)) {
            return;
        }
        try {
            onShowLoading();

            await PostService.publish(publishRequestModel);

            data.onCallBack && data.onCallBack();

            onSuccess(Strings.POST.PUBLISH_SUCCESS);

            onHideLoading();
        } catch (error: any) {
            onHideLoading();
            const errorMessage = Helpers.renderExceptionError(error);
            onError(errorMessage);
        }
    };

    const handleUnpublishPost = async (data: { id: string; onCallBack?: () => void }) => {
        try {
            onShowLoading();

            await PostService.unpublish(data.id);
            data.onCallBack && data.onCallBack();

            onSuccess(Strings.POST.STOP_PUBLISH_SUCCESS);

            onHideLoading();
        } catch (error) {
            onHideLoading();
            const err = Helpers.renderExceptionError(error);
            onError(err);
        }
    };

    const handleDeletePost = (data: { id: string; onCallBack?: () => void }) => {
        Helpers.showConfirmAlert(Strings.POST.CONFIRM_DELETE, async () => {
            try {
                onShowLoading();

                await PostService.delete(data.id);
                data.onCallBack && data.onCallBack();

                onSuccess(Strings.POST.DELETE_SUCCESS);

                onHideLoading();
            } catch (error: any) {
                onHideLoading();
                const err = Helpers.renderExceptionError(error);
                onError(err);
            }
        });
    };

    const getUpdatedFields = (baseData: any, updatedData: any, options?: { allowEmptyArray?: boolean }) => {
        const updatedFields: Partial<any> = {};

        if (Helpers.isNullOrEmpty(updatedData) || Helpers.isNullOrEmpty(baseData)) {
            return updatedData;
        }

        for (const key in updatedData) {
            const baseDataItem = baseData[key];
            const updatedDataItem = updatedData[key];

            if ((key === "id" || key.endsWith("Id")) && !Helpers.isNullOrEmpty(updatedDataItem)) {
                updatedFields[key] = updatedDataItem;
            } else if (typeof baseDataItem === "object") {
                // Case: Array
                if (Array.isArray(baseDataItem) && Array.isArray(updatedDataItem)) {
                    // Case: Arrays are same length
                    if (updatedDataItem.length === baseDataItem.length) {
                        if (options?.allowEmptyArray && updatedDataItem.length === 0) {
                            updatedFields[key] = updatedDataItem;
                        }
                        // Case: Some item is not equal (same index)
                        // Note: Can check for deeper case when item is object or array not primitive value
                        else if (updatedDataItem.some((item: any, index) => item !== baseDataItem[index])) {
                            updatedFields[key] = updatedDataItem;
                        }
                    }
                    // Case: Arrays are not same length
                    else if (updatedDataItem.length !== baseDataItem.length) {
                        updatedFields[key] = updatedData[key];
                    }
                } else {
                    if (Helpers.isNullOrEmpty(baseDataItem)) continue;
                    if (baseDataItem === updatedDataItem) continue;
                    // Case: Object/IMultiLanguageContent
                    const outObject = getUpdatedFields(baseDataItem, updatedDataItem, options) || ({} as any);
                    if (Helpers.isIMultiLanguageContent(updatedDataItem) && Helpers.isIMultiLanguageContent(outObject)) {
                        if (Object.keys(outObject["value"]).length >= 1) {
                            const { code } = updatedDataItem as IMultiLanguageContent;
                            if (Object.keys(outObject["value"]).length > 0) {
                                outObject["code"] = code;
                                updatedFields[key] = outObject as any;
                            }
                        }
                    } else if (Object.keys(outObject).length >= 1) {
                        updatedFields[key] = outObject;
                    }
                }
            } else if (typeof baseDataItem === "number") {
                if (baseDataItem !== updatedDataItem) {
                    updatedFields[key] = updatedDataItem;
                }
            } else if (typeof updatedDataItem === "string" || typeof baseDataItem === "string") {
                if (baseDataItem !== updatedDataItem) {
                    updatedFields[key] = updatedDataItem;
                }
            } else if (Helpers.isNullOrEmpty(baseDataItem)) {
                if (updatedDataItem !== null || updatedDataItem) {
                    updatedFields[key] = updatedDataItem;
                }
            }
        }

        return updatedFields;
    };

    const getDataSubmit = async (baseDataEditModelRef: any, dataPost: any) => {
        let postVideos: IPostVideo[] | undefined = undefined;

        if (dataPost?.postVideos !== baseDataEditModelRef.current?.postVideos) {
            let postVideosMap: any = {};
            postVideos = [...(dataPost?.postVideos || [])];

            for (const item of postVideos) {
                if (item.fileUrl && !postVideosMap[item.fileUrl]) {
                    postVideosMap[item.fileUrl] = item;
                }
            }

            const createUrls = Object.values(postVideosMap).map((item: any) => item.fileUrl) as string[];

            if (Object.values(postVideosMap).length > 0) {
                const urlWithId = await AssetService.createUrlAsset({
                    url: createUrls,
                    serviceCode: serviceCode,
                    tenantCode: tenantCode,
                });
                for (const item of urlWithId) {
                    if (postVideosMap[item.accessUrl]) {
                        postVideosMap[item.accessUrl].fileId = item.id;
                    }
                }
            }

            postVideos = Object.values(postVideosMap);
            postVideos?.sort((item_a, item_b) => {
                if (item_a && item_b && item_a.sequence && item_b.sequence) {
                    return item_a.sequence - item_b.sequence;
                } else {
                    return 0;
                }
            });
            postVideos = postVideos.map((item, index) => ({
                ...item,
                sequence: index + 1,
            }));
        }

        let data: any = getUpdatedFields(
            {
                type: baseDataEditModelRef.current?.type,
                title: baseDataEditModelRef.current?.title,
                shortDescription: baseDataEditModelRef.current?.shortDescription,
                previewImage: baseDataEditModelRef.current?.previewImage,
                previewImageUrl: baseDataEditModelRef.current?.previewImageUrl,
                isHighlighted: baseDataEditModelRef.current?.isHighlighted,
                eventTime: baseDataEditModelRef.current?.eventTime,
                content: baseDataEditModelRef.current?.content,
                postVideos: baseDataEditModelRef.current?.postVideos,
            },
            {
                type: dataPost?.type,
                postVideos: postVideos,
                previewImage: dataPost?.previewImage,
                previewImageUrl: dataPost?.previewImageUrl,
                isHighlighted: dataPost?.isHighlighted,
                eventTime: moment(dataPost?.eventTime).unix() !== 0 ? dataPost?.eventTime : undefined,
                title: Helpers.getDataMultiLanguage({
                    data: dataPost.title,
                    currentLanguage: language,
                    listLanguage: listMultiLanguage,
                }),
                shortDescription: Helpers.getDataMultiLanguage({
                    data: dataPost.shortDescription,
                    currentLanguage: language,
                    listLanguage: listMultiLanguage,
                }),
                content: Helpers.getDataMultiLanguage({
                    data: dataPost.content,
                    currentLanguage: language,
                    listLanguage: listMultiLanguage,
                }),
            }
        );

        if (dataPost?.type !== PostType.News) {
            data.postTag = {
                id: dataPost?.postTag,
            };
        }

        return {
            ...data,
            tenantCode,
            serviceCode,
        };
    };

    return {
        listPostTags,
        listPostTypes,
        listPostHighlighted,
        publishRequestModel,
        setPublishRequestModel,

        handleDeletePost,
        handlePublishPost,
        handleUnpublishPost,

        getDataSubmit,
        getUpdatedFields,
        getDetailPostById,
        getPublishedStatusValue,
    };
};

export default usePost;
